import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faRightFromBracket,
  faTrashAlt,
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { baseURL } from "../config";
import setUpAxios from "../setUpAxios";
import { useLoginContext } from "../Contexts/LoginContext";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper";

function UserInfoSection() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const deleteCancelRef = useRef(null);
  const menuId = useId();
  const navigate = useNavigate();
  const { userName, mobileNumber, profilePicture, role, logout } =
    useLoginContext();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (showDeleteConfirmation) {
        setShowDeleteConfirmation(false);
        window.setTimeout(() => triggerRef.current?.focus(), 0);
      } else if (isMenuOpen) {
        setIsMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen, showDeleteConfirmation]);

  useEffect(() => {
    if (!showDeleteConfirmation) return undefined;

    const focusTimer = window.setTimeout(
      () => deleteCancelRef.current?.focus(),
      0
    );
    return () => window.clearTimeout(focusTimer);
  }, [showDeleteConfirmation]);

  const handleDeleteAccount = async () => {
    try {
      setUpAxios();
      await axios.delete(`${baseURL}/user/delete`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      localStorage.removeItem("token");
      localStorage.setItem("loggedIn", "false");
      navigate("/login");
      window.location.reload();
    } catch (error) {
      console.error("Account deletion failed:", error);
      alert(t("profile.delete_error"));
    }
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    logout();
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsMenuOpen((open) => !open)}
        className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors hover:bg-primary/[0.05] ${focusRing}`}
        aria-label={t("profile.open_menu")}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        aria-controls={isMenuOpen ? menuId : undefined}
      >
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={userName || t("profile.avatar_alt")}
            className={`h-9 w-9 rounded-full border-2 object-cover shadow-sm ${
              isMenuOpen ? "border-primary" : "border-line"
            }`}
          />
        ) : (
          <FontAwesomeIcon
            icon={faUserCircle}
            className={`text-[29px] ${isMenuOpen ? "text-primary" : "text-[#6B7280]"}`}
            aria-hidden="true"
          />
        )}
      </button>

      {isMenuOpen && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          className="absolute right-0 top-full z-[60] mt-2 w-64 overflow-hidden rounded-2xl border border-line bg-white shadow-[0_16px_38px_rgba(31,58,95,0.18)]"
        >
          <div className="border-b border-line bg-paper/60 p-4">
            <div className="flex items-center gap-3">
              {profilePicture ? (
                <img
                  src={profilePicture}
                  alt=""
                  className="h-11 w-11 flex-shrink-0 rounded-full border-2 border-white object-cover shadow-sm"
                  aria-hidden="true"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faUserCircle}
                  className="flex-shrink-0 text-[38px] text-[#6B7280]"
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0">
                {userName && (
                  <p className="truncate text-[13.5px] font-semibold text-[#1A2233]">
                    {userName}
                  </p>
                )}
                {mobileNumber && (
                  <p className="mt-0.5 truncate font-mono text-[11px] text-[#6B7280]">
                    {mobileNumber}
                  </p>
                )}
                {role && (
                  <span className="mt-1.5 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold capitalize text-primary">
                    {role}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1 p-2">
            <MenuButton
              onClick={() => {
                setIsMenuOpen(false);
                setShowDeleteConfirmation(true);
              }}
              className="text-debit hover:bg-debit/[0.06]"
            >
              <FontAwesomeIcon
                icon={faTrashAlt}
                className="w-4 text-debit"
                aria-hidden="true"
              />
              <span>{t("profile.delete_account")}</span>
            </MenuButton>

            <MenuButton
              onClick={handleLogout}
              className="text-[#374151] hover:bg-primary/[0.05] hover:text-primary"
            >
              <FontAwesomeIcon
                icon={faRightFromBracket}
                className="w-4 text-[#6B7280]"
                aria-hidden="true"
              />
              <span>{t("profile.logout")}</span>
            </MenuButton>
          </div>
        </div>
      )}

      {showDeleteConfirmation &&
        createPortal(
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-[#111827]/55 p-4 backdrop-blur-[2px]"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setShowDeleteConfirmation(false);
                window.setTimeout(() => triggerRef.current?.focus(), 0);
              }
            }}
          >
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="delete-account-title"
              aria-describedby="delete-account-description"
              className="w-full max-w-sm rounded-[22px] border border-line bg-white p-5 shadow-2xl"
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-debit/10">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="text-lg text-debit"
                  aria-hidden="true"
                />
              </div>
              <h2
                id="delete-account-title"
                className="mt-4 text-center font-slab text-[18px] font-bold text-[#1A2233]"
              >
                {t("profile.confirm_title")}
              </h2>
              <p
                id="delete-account-description"
                className="mt-2 text-center text-[12.5px] leading-relaxed text-[#6B7280]"
              >
                {t("profile.confirm_body")}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  ref={deleteCancelRef}
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    window.setTimeout(() => triggerRef.current?.focus(), 0);
                  }}
                  className={`min-h-[44px] rounded-xl border border-line px-3 py-2 text-[12.5px] font-semibold text-[#4B5563] hover:bg-paper ${focusRing}`}
                >
                  {t("profile.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  className={`min-h-[44px] rounded-xl bg-debit px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-debit/90 ${focusRing}`}
                >
                  {t("profile.delete")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

const MenuButton = ({ children, className = "", ...props }) => (
  <button
    type="button"
    role="menuitem"
    {...props}
    className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${className}`}
  >
    {children}
  </button>
);

export default UserInfoSection;
